import React, {useCallback, useEffect, useState} from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import routes from '../../routes/routes';
import {useAuthStore} from '../../../common/stores/useAuthStore';
import {useOrganizerStore} from '../../../common/stores/useOrganizerStore';
import {useTagsStore} from '../../../common/stores/useTagsStore';
import {useEventDtoStore, useEventListStore} from '../../../common';
import {
  deleteEvent,
  deletePlace,
  fetchOrganizer,
} from '../../../common/services/places';
import {SearchInput} from '../../search/SearchInput';
import {adminActivitiesCache} from '../../../common/constants/queryCacheKey';
import {
  ActivitySortBy,
  ActivityType,
  AdminActivity,
  fetchAdminActivities,
  SortDirection,
} from '../../../common/services/activities';

const PAGE_SIZE = 20;

const typeOptions: {label: string; value: ActivityType | undefined}[] = [
  {label: 'All', value: undefined},
  {label: 'Places', value: 'place'},
  {label: 'Events', value: 'event'},
];

const sortOptions: {label: string; value: ActivitySortBy}[] = [
  {label: 'Name', value: 'name'},
  {label: 'Going', value: 'going'},
  {label: 'Last updated', value: 'lastUpdated'},
  {label: 'Event date', value: 'date'},
];

const formatDateTime = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const formatDay = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : '';

interface ActivityRowProps {
  activity: AdminActivity;
  isBusy: boolean;
  onEdit: (activity: AdminActivity) => void;
  onDelete: (activity: AdminActivity) => void;
}

const ActivityRow: React.FC<ActivityRowProps> = ({
  activity,
  isBusy,
  onEdit,
  onDelete,
}) => {
  const isEvent = activity.type === 'event';
  // Events are edited through their organizer's screens, so one without an
  // organizer cannot be opened for editing.
  const canEdit = !isEvent || Boolean(activity.organizerId);
  const address = [activity.street, activity.city].filter(Boolean).join(', ');

  return (
    <div style={rowStyle}>
      {activity.photoUri ? (
        <img src={activity.photoUri} alt="" style={thumbnailStyle} />
      ) : (
        <div style={thumbnailStyle} />
      )}

      <div style={rowMainStyle}>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <span style={nameStyle}>{activity.name}</span>
          <span style={isEvent ? eventBadgeStyle : placeBadgeStyle}>
            {isEvent ? 'Event' : 'Place'}
          </span>
        </div>
        <div style={metaStyle}>
          {address || 'No address'}
          {activity.organizerName ? ` · ${activity.organizerName}` : ''}
        </div>
        {isEvent && (
          <div style={metaStyle}>
            {activity.dateTimeStart
              ? `${formatDateTime(activity.dateTimeStart)} – ${formatDateTime(activity.dateTimeStop)}`
              : 'Recurring'}
          </div>
        )}
      </div>

      <div style={statsStyle}>
        <div>
          <strong style={{color: '#fff'}}>{activity.going}</strong> going
        </div>
        {activity.rating != null && <div>★ {activity.rating.toFixed(1)}</div>}
        <div>Updated {formatDay(activity.lastUpdated)}</div>
      </div>

      <div style={actionsStyle}>
        <button
          onClick={() => onEdit(activity)}
          disabled={!canEdit || isBusy}
          title={canEdit ? '' : 'This event has no organizer'}
          style={canEdit ? inactiveChipStyle : disabledChipStyle}>
          Edit
        </button>
        <button
          onClick={() => onDelete(activity)}
          disabled={isBusy}
          style={dangerChipStyle}>
          Delete
        </button>
      </div>
    </div>
  );
};

export const ActivitiesListScreen: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState<ActivityType | undefined>(undefined);
  const [sortBy, setSortBy] = useState<ActivitySortBy>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [pageNumber, setPageNumber] = useState(1);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setOrganizer = useOrganizerStore(state => state.setOrganizer);
  const onChangeViewMode = useAuthStore(state => state.onChangeViewMode);
  const setSelectedEventId = useEventListStore(
    state => state.setSelectedEventId,
  );
  const clearEventDto = useEventDtoStore(state => state.clearEventDto);
  const clearSelectedTagCodes = useTagsStore(
    state => state.clearSelectedTagCodes,
  );

  // The event screens work on one organizer and keep their draft in shared
  // stores; start them from a clean state, in the events view.
  const prepareEventScreens = () => {
    clearEventDto();
    clearSelectedTagCodes();
    onChangeViewMode('events');
  };

  const handleAddEvent = () => {
    prepareEventScreens();
    navigate(routes.organizer, {
      state: {next: routes.createEvent, returnTo: routes.activities},
    });
  };

  const handleAddPlace = () =>
    navigate(routes.organizer, {state: {next: routes.createPlace}});

  const editMutation = useMutation({
    mutationFn: async (activity: AdminActivity) => {
      if (activity.type === 'place') {
        navigate(`${routes.editPlace}?id=${activity.id}`);
        return;
      }

      const organizer = await fetchOrganizer(activity.organizerId ?? '');
      prepareEventScreens();
      setOrganizer(organizer);
      setSelectedEventId(activity.id);
      // Saving or going back returns to this list, not the organizer's events.
      navigate(routes.editEvent, {state: {returnTo: routes.activities}});
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (activity: AdminActivity) =>
      activity.type === 'place'
        ? deletePlace(activity.id)
        : deleteEvent(activity.id, activity.organizerId),
    onSuccess: (_, activity) => {
      setActionError(null);
      setNotice(`"${activity.name}" was deleted.`);
      queryClient.invalidateQueries({queryKey: [adminActivitiesCache]});
    },
    onError: (error: Error) => {
      setNotice(null);
      setActionError(error.message);
    },
  });

  const handleDelete = (activity: AdminActivity) => {
    const kind = activity.type === 'event' ? 'event' : 'place';
    if (
      window.confirm(
        `Delete the ${kind} "${activity.name}"? This also removes its bookings and cannot be undone.`,
      )
    ) {
      deleteMutation.mutate(activity);
    }
  };

  // Any change to the search or filters starts again from the first page.
  const handleSearch = useCallback((value: string) => {
    setKeyword(value);
    setPageNumber(1);
  }, []);

  const handleTypeChange = (value: ActivityType | undefined) => {
    setType(value);
    setPageNumber(1);
  };

  const handleSortByChange = (value: ActivitySortBy) => {
    setSortBy(value);
    setPageNumber(1);
  };

  const toggleSortDirection = () => {
    setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
    setPageNumber(1);
  };

  const {data, isPending, isFetching, isError} = useQuery({
    queryKey: [
      adminActivitiesCache,
      keyword,
      type,
      sortBy,
      sortDirection,
      pageNumber,
    ],
    queryFn: () =>
      fetchAdminActivities({
        keyword,
        type,
        sortBy,
        sortDirection,
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  const maxPage = data?.maxPage ?? 0;
  const totalItems = data?.totalItems ?? 0;

  // Deleting the last row of the last page leaves a page past the end.
  useEffect(() => {
    if (data && pageNumber > Math.max(data.maxPage, 1)) {
      setPageNumber(Math.max(data.maxPage, 1));
    }
  }, [data, pageNumber]);

  return (
    <div style={screenStyle}>
      <div style={{maxWidth: '900px', margin: '0 auto'}}>
        <h1 style={{color: '#fff', textAlign: 'center', marginBottom: '30px'}}>
          Activities
        </h1>

        <div style={headerRowStyle}>
          <div style={searchWrapperStyle}>
            <SearchInput
              placeholder="Search places and events..."
              onSearch={handleSearch}
            />
          </div>
          <button onClick={handleAddEvent} style={activeChipStyle}>
            Add event
          </button>
          <button onClick={handleAddPlace} style={activeChipStyle}>
            Add place
          </button>
        </div>

        {actionError && <p style={errorStyle}>{actionError}</p>}
        {notice && <p style={noticeStyle}>{notice}</p>}

        <div style={toolbarStyle}>
          <div style={{display: 'flex', gap: '8px'}}>
            {typeOptions.map(option => (
              <button
                key={option.label}
                onClick={() => handleTypeChange(option.value)}
                style={
                  type === option.value ? activeChipStyle : inactiveChipStyle
                }>
                {option.label}
              </button>
            ))}
          </div>

          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <label htmlFor="activities-sort" style={{color: '#999'}}>
              Sort by
            </label>
            <select
              id="activities-sort"
              value={sortBy}
              onChange={e =>
                handleSortByChange(e.target.value as ActivitySortBy)
              }
              style={selectStyle}>
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={toggleSortDirection}
              style={inactiveChipStyle}
              aria-label={
                sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'
              }>
              {sortDirection === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
          </div>
        </div>

        <p style={{color: '#999', margin: '0 0 16px'}}>
          {isPending
            ? 'Loading...'
            : `${totalItems} ${totalItems === 1 ? 'activity' : 'activities'}`}
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            opacity: isFetching && !isPending ? 0.6 : 1,
          }}>
          {items.map(activity => (
            <ActivityRow
              key={`${activity.type}-${activity.id}`}
              activity={activity}
              isBusy={editMutation.isPending || deleteMutation.isPending}
              onEdit={editMutation.mutate}
              onDelete={handleDelete}
            />
          ))}
        </div>

        {isError && (
          <p style={messageStyle}>Could not load activities. Try again.</p>
        )}

        {!isError && !isPending && items.length === 0 && (
          <p style={messageStyle}>No activities match your search.</p>
        )}

        {maxPage > 1 && (
          <div style={paginationStyle}>
            <button
              onClick={() => setPageNumber(page => page - 1)}
              disabled={pageNumber <= 1}
              style={pageNumber <= 1 ? disabledChipStyle : inactiveChipStyle}>
              Previous
            </button>
            <span style={{color: '#999'}}>
              Page {pageNumber} of {maxPage}
            </span>
            <button
              onClick={() => setPageNumber(page => page + 1)}
              disabled={pageNumber >= maxPage}
              style={
                pageNumber >= maxPage ? disabledChipStyle : inactiveChipStyle
              }>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const screenStyle: React.CSSProperties = {
  backgroundColor: '#1a1a1a',
  minHeight: '100vh',
  padding: '40px 20px',
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  margin: '24px 0',
};

const chipBaseStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: '8px',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'all 0.2s ease',
};

const inactiveChipStyle: React.CSSProperties = {
  ...chipBaseStyle,
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #333',
};

const activeChipStyle: React.CSSProperties = {
  ...chipBaseStyle,
  backgroundColor: '#daff00',
  color: '#1a1a1a',
  border: '1px solid #daff00',
};

const disabledChipStyle: React.CSSProperties = {
  ...inactiveChipStyle,
  color: '#555',
  cursor: 'not-allowed',
};

const dangerChipStyle: React.CSSProperties = {
  ...chipBaseStyle,
  backgroundColor: 'transparent',
  color: '#ff4d4d',
  border: '1px solid #ff4d4d',
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
};

// The search takes its own line on narrow screens instead of shrinking.
const searchWrapperStyle: React.CSSProperties = {
  flex: '1 1 300px',
  maxWidth: '600px',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  flexShrink: 0,
};

const errorStyle: React.CSSProperties = {
  color: '#ff4d4d',
  margin: '16px 0 0',
};

const noticeStyle: React.CSSProperties = {
  color: '#daff00',
  margin: '16px 0 0',
};

const selectStyle: React.CSSProperties = {
  height: '40px',
  padding: '0 12px',
  borderRadius: '8px',
  border: '1px solid #333',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  fontSize: '0.85rem',
};

// On narrow screens the stats and buttons wrap under the name.
const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '16px',
  padding: '14px',
  borderRadius: '12px',
  backgroundColor: '#242424',
  border: '1px solid #333',
};

const thumbnailStyle: React.CSSProperties = {
  width: '64px',
  height: '64px',
  borderRadius: '8px',
  objectFit: 'cover',
  backgroundColor: '#333',
  flexShrink: 0,
};

const nameStyle: React.CSSProperties = {
  color: '#fff',
  fontWeight: 'bold',
  fontSize: '1.05rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const badgeBaseStyle: React.CSSProperties = {
  padding: '2px 10px',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  flexShrink: 0,
};

const placeBadgeStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  backgroundColor: '#333',
  color: '#ddd',
};

const eventBadgeStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  backgroundColor: '#daff00',
  color: '#1a1a1a',
};

const metaStyle: React.CSSProperties = {
  color: '#999',
  fontSize: '0.85rem',
  marginTop: '4px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowMainStyle: React.CSSProperties = {
  flex: '1 1 200px',
  minWidth: 0,
};

const statsStyle: React.CSSProperties = {
  marginLeft: 'auto',
  color: '#999',
  fontSize: '0.8rem',
  textAlign: 'right',
  flexShrink: 0,
  lineHeight: 1.6,
};

const messageStyle: React.CSSProperties = {
  color: '#666',
  textAlign: 'center',
  fontSize: '1.3rem',
  marginTop: '40px',
};

const paginationStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  marginTop: '30px',
};
